import { Component, OnDestroy } from '@angular/core'
import { Title } from '@angular/platform-browser'
import { NavigationEnd } from '@angular/router'
import { IonRouterOutlet } from '@ionic/angular'
import { Subscription } from 'rxjs'
import { RouterLoggingParams } from 'src/app/interfaces/log'
import { LogService } from 'src/app/services/log/log.service'
import { GeneralService } from '../services/general/general.service'
import { LocaleService } from '../services/locale/locale-service'
import { ModalService } from '../services/modal/modal.service'
import { NavigateType, RouterService } from '../services/router/router.service'

@Component({
  template: '',
})
export abstract class Page implements OnDestroy {
  abstract screenName: string
  protected blockBackButton = false
  private backButtonClickCount = 0
  private deeplinkSubscription: Subscription | undefined
  private androidBackButtonSubscription: Subscription | undefined
  private htmlTitle: string | undefined
  private routerSubscription: Subscription | undefined
  private currentUrl = ''
  private didEnter = false
  private willLeave = false
  private hasSetTitle = false // 타이틀 설정 여부

  protected constructor(
    protected generalService: GeneralService,
    protected routerService: RouterService,
    protected routerOutlet: IonRouterOutlet,
    protected modalService: ModalService,
    protected title: Title,
    protected logService: LogService,
    protected localeService: LocaleService
  ) {}

  get isApp(): boolean {
    return this.generalService.isApp
  }

  protected blockSwipeGesture(): void {
    if (this.isApp) {
      this.routerOutlet.swipeGesture = false
    }
  }

  protected enableSwipeGesture(): void {
    if (this.isApp) {
      this.routerOutlet.swipeGesture = true
    }
  }

  protected setTitle(value: string, interpolateParams?: Object): void {
    if (this.hasSetTitle) {
      return
    }
    this.hasSetTitle = true
    this.localeService.getPageTitle(value, interpolateParams).then((localeTitle) => {
      // : 타입스 없으면 뒤에 붙여주기 (통일시킴)
      const title = /:\s?(타입스|types)/.test(value.toLowerCase())
        ? localeTitle
        : `${localeTitle} : ${this.localeService.getLocaleString('타입스')}`
      this.htmlTitle = title
      this.title.setTitle(title)
    })
  }

  protected beforeCloseModal(): boolean {
    return false
  }

  protected afterCloseModal(): boolean {
    return false
  }

  // modal이 열려있을 경우의 backbutton 처리 후 custom backbutton 함수(onBackButton) 수행.
  // 만약 modal 열려있다면 modal만 종료시키고 custom backbutton은 미수행
  async onBackButton(
    navigateType: NavigateType = 'user_action',
    loggingParams?: RouterLoggingParams,
    backCount = 0
  ): Promise<void> {
    if (this.blockBackButton) {
      return
    }
    if (this.beforeCloseModal()) {
      return
    }
    if (this.modalService !== undefined) {
      // modal 열렸을 경우
      if (await this.modalService.dismissTopModal()) {
        return
      }
    }
    if (this.afterCloseModal()) {
      return
    }
    this.navigateBack(navigateType, loggingParams, backCount)
  }

  /**
   * Back button subscription for android
   * Deeplink navigation
   */
  ionViewDidEnter() {
    if (!this.didEnter) {
      this.didEnter = true
      this.willLeave = false
      this.currentUrl = window.location.href
      this.routerSubscription = this.routerService.getRouterSubject().subscribe((navigationData: NavigationEnd) => {
        if (this.currentUrl.includes(navigationData.url)) {
          this.unsubscribeRouterSubscription()
          this.ionViewDidEnter()
        } else {
          this.ionViewWillLeave()
        }
      })
    } else {
      return
    }

    this.logService.setSuperProperty('screen_name', this.screenName)
    this.enableSwipeGesture()

    if (this.htmlTitle !== undefined) {
      this.setTitle(this.htmlTitle)
    }
    const deeplinkPath = this.generalService.deeplink.deeplinkPath
    if (deeplinkPath !== undefined) {
      this.navigateToDeeplinkPath(deeplinkPath)
    }
    this.setDeeplinkSubscription()

    if (this.generalService.isAndroid) {
      this.generalService.increasePriority()

      this.androidBackButtonSubscription = this.generalService.blockingBackButton(undefined, () => {
        this.onBackButton()
      })
    }
  }

  // Remove android back button subscription
  ionViewWillLeave() {
    if (!this.willLeave) {
      this.customOnViewWillLeave()
    }
  }

  ngOnDestroy() {
    if (this.routerSubscription !== undefined) {
      this.routerSubscription.unsubscribe()
    }
  }

  navigateBack(navigateType: NavigateType = 'user_action', loggingParams?: RouterLoggingParams, backCount = 0): void {
    this.routerService.navigateBack(this.routerOutlet, navigateType, loggingParams, backCount)
  }

  protected async customOnViewWillLeave(): Promise<void> {
    this.willLeave = true
    this.didEnter = false
    this.hasSetTitle = false
    this.generalService.closeDrawer()
    if (this.generalService.isAndroid) {
      this.unsubscribeAndroidBackButtonSubscription()
    }
    if (this.modalService !== undefined) {
      this.modalService.dismissAll()
    }
    this.unsubscribeDeeplink()
  }

  private navigateToDeeplinkPath(path: string): void {
    this.routerService.navigate(path.split('?')[0])
    this.generalService.deeplink.initializeDeeplinkPath()
  }

  private setDeeplinkSubscription(): void {
    this.deeplinkSubscription = this.generalService.deeplink
      .getDeeplinkPathSubject()
      .subscribe((path: string | undefined) => {
        if (path !== undefined) {
          this.navigateToDeeplinkPath(path)
        }
      })
  }

  private unsubscribeAndroidBackButtonSubscription(): void {
    if (this.androidBackButtonSubscription !== undefined) {
      this.androidBackButtonSubscription.unsubscribe()
    }
  }

  private unsubscribeRouterSubscription(): void {
    if (this.routerSubscription !== undefined) {
      this.routerSubscription.unsubscribe()
    }
  }
  private unsubscribeDeeplink(): void {
    if (this.deeplinkSubscription !== undefined) {
      this.deeplinkSubscription.unsubscribe()
    }
  }
}
